import type { Address } from '@elara/domain/models/schemas/shared-schemas';

import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';
import { AddressSchema } from '@elara/domain/models/schemas/shared-schemas';

import { getCountriesList } from '@elara/client-core/lib/country-utils';

import { FloatingInput, FloatingSelect } from './floating-field.js';
import { Label } from './label.js';
import { SelectItem } from './select.js';

export interface AddressFieldsProps {
  value?: Address | null;
  onChange: (address: Address | null | undefined) => void;
  className?: string;
  disabled?: boolean;
}

const COUNTRIES = getCountriesList();

/**
 * Check if address has any data entered
 */
function hasAnyData(addr: Partial<Address>): boolean {
  return Object.values(addr).some((v) => v?.trim());
}

/**
 * Build a clean Address from the draft, dropping empty / whitespace fields.
 * Partial addresses are allowed — the schema doesn't require any specific
 * combination, and onboarding already stores a city on its own.
 */
function buildAddress(addr: Partial<Address>): Address | null {
  if (!hasAnyData(addr)) return null;
  const result = AddressSchema.safeParse(addr);
  if (!result.success) return null;
  const parsed = result.data;
  const clean: Address = {};
  if (parsed.city?.trim()) clean.city = parsed.city.trim();
  if (parsed.country?.trim()) clean.country = parsed.country.trim();
  if (parsed.countryCode?.trim()) clean.countryCode = parsed.countryCode.trim();
  if (parsed.line1?.trim()) clean.line1 = parsed.line1.trim();
  if (parsed.line2?.trim()) clean.line2 = parsed.line2.trim();
  if (parsed.postalCode?.trim()) clean.postalCode = parsed.postalCode.trim();
  return Object.keys(clean).length > 0 ? clean : null;
}

/**
 * AddressFields component - a simple form for entering address details.
 * Every field is optional: it emits whatever the user filled in, or null once cleared.
 */
export const AddressFields = React.memo(({ value, onChange, className, disabled }: AddressFieldsProps) => {
  const [draft, setDraft] = useState<Partial<Address>>(value || {});
  const isEditingRef = useRef(false);

  // Sync draft with external value only when not currently being edited by the user
  // (to preserve typing, including spaces)
  useEffect(() => {
    if (value && !isEditingRef.current) setDraft(value);
    isEditingRef.current = false;
  }, [value]);

  const handleFieldChange = (field: keyof Address, fieldValue: string) => {
    isEditingRef.current = true;
    const updatedDraft = { ...draft, [field]: fieldValue || undefined };
    setDraft(updatedDraft);
    onChange(buildAddress(updatedDraft));
  };

  const handleCountryChange = (countryCode: string) => {
    const country = COUNTRIES.find((c) => c.code === countryCode);
    if (country) {
      isEditingRef.current = true;
      const updatedDraft = { ...draft, country: country.name, countryCode: country.code };
      setDraft(updatedDraft);
      onChange(buildAddress(updatedDraft));
    }
  };

  return (
    <div className={cn('space-y-3 @container', disabled && 'opacity-50 pointer-events-none', className)}>
      <Label>Address</Label>

      <FloatingInput
        id="address-line1"
        label="Address line 1"
        value={draft.line1 || ''}
        onChange={(e) => handleFieldChange('line1', e.target.value)}
        disabled={disabled}
      />

      <FloatingInput
        id="address-line2"
        label="Apt, Suite, etc. (optional)"
        value={draft.line2 || ''}
        onChange={(e) => handleFieldChange('line2', e.target.value)}
        disabled={disabled}
      />

      <div className="grid grid-cols-1 @md:grid-cols-2 gap-3">
        <FloatingInput
          id="address-city"
          label="City"
          value={draft.city || ''}
          onChange={(e) => handleFieldChange('city', e.target.value)}
          disabled={disabled}
        />

        <FloatingInput
          id="address-postalCode"
          label="Postal code"
          value={draft.postalCode || ''}
          onChange={(e) => handleFieldChange('postalCode', e.target.value)}
          disabled={disabled}
        />
      </div>

      <FloatingSelect
        id="address-country"
        label="Country"
        value={draft.countryCode || ''}
        onValueChange={handleCountryChange}
        disabled={disabled}
      >
        {COUNTRIES.map((country) => (
          <SelectItem key={country.code} value={country.code}>
            {country.name} ({country.code})
          </SelectItem>
        ))}
      </FloatingSelect>
    </div>
  );
});

AddressFields.displayName = 'AddressFields';
